import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X, Map, Edit2 } from 'lucide-react';
import { generateId } from '@/shared/lib/id';
import { Partition } from '@/shared/model';
import { roundTo } from '@/shared/lib/number';

interface PartitionsManagerProps {
  partitions: Partition[];
  onChange: (partitions: Partition[]) => void;
  roomHeight: number;
}

export const PartitionsManager: FC<PartitionsManagerProps> = ({ 
  partitions, 
  onChange,
  roomHeight 
}) => {
  const { t } = useTranslation();
  
  const [isAddingPartition, setIsAddingPartition] = useState(false);
  const [newPartition, setNewPartition] = useState({ 
    name: '', length: '' as string | number, height: '' as string | number, thickness: 10 as string | number, ends: 1 as string | number 
  });
  const [editingPartitionId, setEditingPartitionId] = useState<string | null>(null);

  // Открытие формы добавления: высота автоматически подставляется из высоты помещения
  // (в см), но остаётся редактируемой — если у перегородки кастомная высота (запрошено).
  const startAddPartition = () => {
      setEditingPartitionId(null);
      setNewPartition({
          name: '',
          length: '',
          height: roomHeight ? Math.round(roomHeight * 100) : '',
          thickness: 10,
          ends: 1,
      });
      setIsAddingPartition(true);
  };

  const handleSavePartition = () => {
      const rawLen = newPartition.length;
      const lenStr = (rawLen !== undefined && rawLen !== null && rawLen !== '') ? String(rawLen).trim() : '';
      if (!lenStr || !onChange) {
          return;
      }
      
      const lengthM = parseFloat(lenStr) / 100;
      if (isNaN(lengthM) || lengthM <= 0) {
          return;
      }

      const rawHeight = newPartition.height;
      const heightM = (rawHeight !== '' && rawHeight !== undefined && rawHeight !== null) 
          ? parseFloat(String(rawHeight)) / 100 
          : null;

      const partitionData = {
          name: newPartition.name || t('room_input.partition_default_name', 'Перегородка'),
          length: lengthM,
          height: heightM,
          thickness: parseFloat(String(newPartition.thickness)) || 10,
          ends: parseInt(String(newPartition.ends)) || 1
      };

      if (editingPartitionId) {
          onChange(partitions.map(p => p.id === editingPartitionId ? { ...p, ...partitionData } : p));
          setEditingPartitionId(null);
      } else {
          const generated = generateId();
          onChange([...partitions, { ...partitionData, id: generated }]);
      }

      setNewPartition({ name: '', length: '', height: '', thickness: 10, ends: 1 });
      setIsAddingPartition(false);
  };

  const handleEditPartition = (p: Partition) => {
      // Переводим обратно из метров в см для инпута
      setNewPartition({
          name: p.name || '',
          length: Math.round(Number(p.length) * 100),
          height: p.height ? Math.round(Number(p.height) * 100) : '',
          thickness: p.thickness || 10,
          ends: p.ends || 1
      });
      setEditingPartitionId(p.id || null);
      setIsAddingPartition(true);
  };

  return (
    <div className="mb-6 border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex justify-between items-center mb-3">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('room_input.partitions_title', 'Внутренние перегородки')}</div>
            {!isAddingPartition && (
                <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={startAddPartition}
                    className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded transition-colors"
                >
                    <Plus size={14}/> {t('common.add', 'Добавить')}
                </motion.button>
            )}
        </div>

        <AnimatePresence>
            {isAddingPartition && (
                <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700 mb-3 overflow-hidden"
                >
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{t('room_input.new_partition', 'Новая перегородка')}</span>
                        <button 
                            onClick={() => { setIsAddingPartition(false); setEditingPartitionId(null); }} 
                            className="p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
                            aria-label={t('common.close', 'Закрыть')}
                        >
                            <X size={16} aria-hidden="true"/>
                        </button>
                    </div>
                    <div className="space-y-2">
                        <input 
                            className="w-full p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            placeholder={t('room_input.partition_name_placeholder', 'Название')}
                            value={newPartition.name}
                            onChange={e => setNewPartition({...newPartition, name: e.target.value})}
                        />
                        <div className="flex gap-2">
                            <input 
                                type="number" step="1" placeholder={t('room_input.length_cm_placeholder', 'Длина (см)')}
                                className="w-1/2 p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                                value={newPartition.length}
                                onChange={e => setNewPartition({...newPartition, length: e.target.value})}
                            />
                            <input 
                                type="number" step="1" placeholder={t('room_input.height_cm_placeholder', 'Высота (см)')}
                                className="w-1/2 p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                                value={newPartition.height}
                                onChange={e => setNewPartition({...newPartition, height: e.target.value})}
                            />
                        </div>
                        <div className="flex gap-2">
                            <div className="w-1/2 relative">
                                <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase absolute -top-1.5 left-2 bg-slate-50 dark:bg-slate-800 px-1">{t('room_input.thickness_label', 'Толщина (см)')}</label>
                                <input 
                                    type="number"
                                    className="w-full p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                                    value={newPartition.thickness}
                                    onChange={e => setNewPartition({...newPartition, thickness: e.target.value})}
                                />
                            </div>
                            <div className="w-1/2 relative">
                                <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase absolute -top-1.5 left-2 bg-slate-50 dark:bg-slate-800 px-1">{t('room_input.ends_label', 'Торцы (шт)')}</label>
                                <select 
                                    className="w-full p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 bg-white dark:text-slate-100"
                                    value={newPartition.ends}
                                    onChange={e => setNewPartition({...newPartition, ends: e.target.value})}
                                >
                                    <option value="1">{t('room_input.ends_option_1', '1 (Обычная)')}</option>
                                    <option value="2">{t('room_input.ends_option_2', '2 (Т-образная)')}</option>
                                    <option value="4">{t('room_input.ends_option_4', '4 (Колонна)')}</option>
                                </select>
                            </div>
                        </div>
                        <button 
                            onClick={handleSavePartition}
                            className="w-full py-2 bg-blue-600 text-white rounded font-bold text-sm"
                        >
                            {editingPartitionId ? t('common.save', 'Сохранить') : t('common.add', 'Добавить')}
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>

        <div className="space-y-2">
            {partitions.map(p => {
                const pHeight = (p.height !== undefined && p.height !== null) ? Number(p.height) : Number(roomHeight || 0);
                const pArea = Number(p.ends) === 4 ? 0 : Number(p.length) * pHeight * 2;
                
                return (
                    <motion.div 
                        key={p.id}
                        layout
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded text-sm shadow-sm group"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                                <Map size={16}/>
                            </div>
                            <div>
                                <div className="font-bold text-slate-700 dark:text-slate-200 text-xs">{p.name}</div>
                                {/* eslint-disable i18next/no-literal-string */}
                                <div className="text-slate-500 dark:text-slate-400 text-[10px]">
                                    <span className="font-mono tabular-nums">{p.length}</span>{t('common.meter_short', 'м')} x <span className="font-mono tabular-nums">{pHeight}</span>{t('common.meter_short', 'м')} • <span className="font-mono tabular-nums">{p.thickness}</span>{t('common.cm', 'см')} • <span className="font-mono tabular-nums">{p.ends}</span>т = <span className="font-bold text-slate-600 dark:text-slate-300 font-mono tabular-nums">+{roundTo(pArea, 2)}</span> {t('common.unit_m2', 'м²')}
                                </div>
                                {/* eslint-enable i18next/no-literal-string */}
                            </div>
                        </div>
                        <div className="flex gap-1">
                            <motion.button 
                                whileHover={{ scale: 1.1 }} 
                                whileTap={{ scale: 0.9 }} 
                                onClick={() => handleEditPartition(p)} 
                                className="p-2 text-slate-400 hover:text-blue-500 rounded-lg transition-colors cursor-pointer"
                                aria-label={t('common.edit', 'Редактировать')}
                            >
                                <Edit2 size={16} aria-hidden="true"/>
                            </motion.button>
                            <motion.button 
                                whileHover={{ scale: 1.1 }} 
                                whileTap={{ scale: 0.9 }} 
                                onClick={() => onChange(partitions.filter(x => x.id !== p.id))} 
                                className="p-2 text-slate-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                                aria-label={t('common.delete', 'Удалить')}
                            >
                                <Trash2 size={16} aria-hidden="true"/>
                            </motion.button>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    </div>
  );
};

